import type {
  ReportBoardFilters,
  ReportCatalog,
  ReportMeasure,
  ReportQuery,
  ReportQueryFilter,
  ReportRecordsRequest,
  ReportTile,
  ReportTileChart,
  ReportTileView
} from "@gettactic/api";
import { formatValue } from "./formatValue";

/** The filter in place of any on the same dimension. */
const replaceFilter = (
  filters: ReportQueryFilter[] | undefined,
  filter: ReportQueryFilter
): ReportQueryFilter[] => [
  ...(filters ?? []).filter((f) => f.dimension !== filter.dimension),
  filter
];

/** The board filters a tile can take: the office scope and, of the rest, those on its dimensions. */
const applicableFilters = (
  filters: ReportBoardFilters,
  dimensions: string[] | undefined
): ReportQueryFilter[] => {
  const out: ReportQueryFilter[] = [];
  if (filters.office_ids.length > 0 && dimensions?.includes("office")) {
    out.push({ dimension: "office", in: filters.office_ids });
  }
  for (const f of filters.filters ?? []) {
    if (f.in.length > 0 && dimensions?.includes(f.dimension)) {
      out.push(f);
    }
  }
  return out;
};

/**
 * A tile shown another way. A measure tile keeps how it is drawn as a
 * chart whatever view it shows, so a switch away and back loses nothing
 * (a table or a number draws the pair meanwhile); a map has no chart.
 */
export const withView = (
  tile: ReportTile,
  view: ReportTileView
): ReportTile => ({
  ...tile,
  view,
  chart: view === "map" ? null : tile.chart
});

/** The dimensions a query touches: its breakdowns and its filters. */
const cutsOf = (query: ReportQuery): string[] => [
  ...(query.breakdown ?? []),
  ...(query.filters ?? []).map((f) => f.dimension)
];

/** Whether the measure's facts cannot serve two of these dimensions together. */
const clashes = (
  measure: ReportMeasure | undefined,
  dimensions: string[]
): boolean =>
  (measure?.conflicts ?? []).some(
    ([a, b]) => dimensions.includes(a) && dimensions.includes(b)
  );

export type TargetProgress = {
  isMet: boolean;
  /** How far along, 0..1, for a bar; null for "lower is better" measures. */
  ratio: number | null;
  label: string;
};

/**
 * Where a value stands against a tile's target. "Higher is better" measures
 * fill a bar towards the target; "lower is better" ones only say whether
 * they are under it and by how much they miss.
 */
export const targetProgress = (
  measure: Pick<ReportMeasure, "kind" | "good_when">,
  value: number,
  target: number
): TargetProgress => {
  const shown = formatValue(measure, target);
  if (measure.good_when === "down") {
    const isMet = value <= target;
    return {
      isMet,
      ratio: null,
      label: isMet
        ? `Within the ${shown} target`
        : `Over the ${shown} target by ${formatValue(measure, value - target)}`
    };
  }
  const isMet = value >= target;
  return {
    isMet,
    ratio: target > 0 ? Math.max(0, Math.min(1, value / target)) : 1,
    label: isMet
      ? `Target of ${shown} met`
      : `${formatValue(measure, value)} of ${shown} target`
  };
};

/**
 * Narrows a tile's query by the board filters, mirroring the server rule:
 * board offices replace the tile's office filter, any other board filter
 * replaces the tile's on that dimension (both only where the measure has
 * the dimension, and the tile's own cut can take it), a board period
 * replaces the tile's period. A calendar tile keeps reading day by day
 * whatever period the board sets.
 */
export const applyBoardFilters = (
  catalog: ReportCatalog,
  query: ReportQuery,
  filters: ReportBoardFilters,
  chart?: ReportTileChart | null
): ReportQuery => {
  let next = query;
  const measure = catalog.measures.find((m) => m.key === query.measure);
  for (const f of applicableFilters(filters, measure?.dimensions)) {
    // A filter the tile's cut cannot take (a person on a by-desk tile)
    // leaves the tile alone rather than break it, as the server does.
    if (clashes(measure, [...cutsOf(next), f.dimension])) {
      continue;
    }
    next = { ...next, filters: replaceFilter(next.filters, f) };
  }
  if (filters.period) {
    next = { ...next, period: filters.period };
  }
  if (chart?.form === "calendar" && next.period.grain !== "day") {
    next = { ...next, period: { ...next.period, grain: "day" } };
  }
  return next;
};

/**
 * Board filters for a map tile: the period follows the board, the place
 * does not. A map is about its floor or its office, and the board's offices
 * would move it somewhere else.
 */
export const applyBoardFiltersToMap = (
  query: ReportQuery,
  filters: ReportBoardFilters
): ReportQuery =>
  filters.period ? { ...query, period: filters.period } : query;

/**
 * The query a tile runs under the board's filters: a map keeps its place
 * and follows the period, everything else takes every filter it can. Null
 * for a tile without a query.
 */
export const applyBoardFiltersToTile = (
  catalog: ReportCatalog,
  tile: Pick<ReportTile, "view" | "query" | "chart">,
  filters: ReportBoardFilters
): ReportQuery | null => {
  if (!tile.query) {
    return null;
  }
  return tile.view === "map"
    ? applyBoardFiltersToMap(tile.query, filters)
    : applyBoardFilters(catalog, tile.query, filters, tile.chart);
};

/** The records counterpart of applyBoardFilters; timeless datasets keep no period. */
export const applyBoardFiltersToRecords = (
  catalog: ReportCatalog,
  request: ReportRecordsRequest,
  filters: ReportBoardFilters
): ReportRecordsRequest => {
  const dataset = catalog.datasets.find((d) => d.key === request.dataset);
  let next = request;
  for (const f of applicableFilters(filters, dataset?.dimensions)) {
    next = { ...next, filters: replaceFilter(next.filters, f) };
  }
  if (filters.period && dataset && !dataset.timeless) {
    next = { ...next, period: filters.period };
  }
  return next;
};

/**
 * The dimensions a board can be filtered by, in catalog order: any that
 * some tile's measure or dataset takes, offices aside (they have their own
 * picker). A dimension nobody on the board can take would filter nothing.
 */
export const boardFilterDimensions = (
  catalog: ReportCatalog,
  tiles: ReportTile[]
): string[] => {
  const taken = new Set<string>();
  for (const tile of tiles) {
    const dimensions = tile.records
      ? catalog.datasets.find((d) => d.key === tile.records?.dataset)
          ?.dimensions
      : catalog.measures.find((m) => m.key === tile.query?.measure)?.dimensions;
    for (const key of dimensions ?? []) {
      taken.add(key);
    }
  }
  return catalog.dimensions
    .map((d) => d.key)
    .filter((key) => key !== "office" && taken.has(key));
};

/** Moves the tile at `from` to `to`, keeping every other tile in order. */
export const reorderTiles = (
  tiles: ReportTile[],
  from: number,
  to: number
): ReportTile[] => {
  if (from === to || from < 0 || to < 0 || from >= tiles.length) {
    return tiles;
  }
  const next = [...tiles];
  const [moved] = next.splice(from, 1);
  next.splice(Math.min(to, next.length), 0, moved);
  return next;
};

/**
 * Display order of a board: the anchor tile (the one the board is about)
 * comes first, everything else keeps its order. Without a matching anchor
 * the order is untouched.
 */
export const withAnchorFirst = (
  tiles: ReportTile[],
  anchorTileId: string
): ReportTile[] => {
  const anchor = tiles.find((t) => t.id === anchorTileId);
  if (!anchor) {
    return tiles;
  }
  return [anchor, ...tiles.filter((t) => t.id !== anchorTileId)];
};

/**
 * The saved order behind a display order: the anchor goes back to where it
 * is stored and everything else takes the order it was shown in, so editing
 * the board never bakes the anchor-first display into it.
 */
export const savedOrder = (
  display: ReportTile[],
  saved: ReportTile[],
  anchorTileId: string
): ReportTile[] => {
  const anchorIndex = saved.findIndex((t) => t.id === anchorTileId);
  const anchor = display.find((t) => t.id === anchorTileId);
  if (anchorIndex < 0 || !anchor) {
    return display;
  }
  const next = display.filter((t) => t.id !== anchorTileId);
  next.splice(Math.min(anchorIndex, next.length), 0, anchor);
  return next;
};

export const replaceTile = (
  tiles: ReportTile[],
  tile: ReportTile
): ReportTile[] => tiles.map((t) => (t.id === tile.id ? tile : t));

/** A section: a heading that groups the tiles under it until the next section. */
export const isSection = (tile: Pick<ReportTile, "view">): boolean =>
  tile.view === "section";

/** A new section, full width, asking nothing. */
export const sectionTile = (title: string): ReportTile => ({
  id: newTileId(),
  title,
  size: 4,
  view: "section",
  query: null,
  records: null,
  chart: null,
  target: null
});

/**
 * The tiles a reader has folded away: everything under a collapsed section
 * up to the next section. Sections themselves always show.
 */
export const hiddenByCollapsed = (
  tiles: ReportTile[],
  collapsed: ReadonlySet<string>
): Set<string> => {
  const hidden = new Set<string>();
  let isFolded = false;
  for (const tile of tiles) {
    if (isSection(tile)) {
      isFolded = collapsed.has(tile.id);
      continue;
    }
    if (isFolded) {
      hidden.add(tile.id);
    }
  }
  return hidden;
};

/** How many tiles sit under the section at `index`, up to the next section. */
export const sectionSize = (tiles: ReportTile[], index: number): number => {
  let count = 0;
  for (let i = index + 1; i < tiles.length && !isSection(tiles[i]); i++) {
    count++;
  }
  return count;
};

export type OfficeFloor = { id: string; label: string; office: string };

/** Floors gathered under their office, in the order the floors came. */
export const floorsByOffice = (
  floors: OfficeFloor[]
): { office: string; floors: OfficeFloor[] }[] => {
  const groups: { office: string; floors: OfficeFloor[] }[] = [];
  for (const floor of floors) {
    const group = groups.find((g) => g.office === floor.office);
    if (group) {
      group.floors.push(floor);
    } else {
      groups.push({ office: floor.office, floors: [floor] });
    }
  }
  return groups;
};

/** The floors of the offices given, in catalog order, each with its office's name. */
export const floorsOfOffices = (
  catalog: ReportCatalog,
  officeIds: string[]
): OfficeFloor[] => {
  const offices = new Map(
    (catalog.dimensions.find((d) => d.key === "office")?.members ?? []).map(
      (m) => [m.id, m.label]
    )
  );
  return (catalog.dimensions.find((d) => d.key === "floor")?.members ?? [])
    .filter((f) => !!f.parent && officeIds.includes(f.parent))
    .map((f) => ({
      id: f.id,
      label: f.label,
      office: offices.get(f.parent ?? "") ?? ""
    }));
};

export const EMPTY_BOARD_FILTERS: ReportBoardFilters = {
  office_ids: [],
  period: null,
  filters: []
};

/** Short, unique enough within one board; the server only needs uniqueness. */
export const newTileId = (): string =>
  `t_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** The query parameter that names the open board in the address. */
export const BOARD_PARAM = "board";

/**
 * Writes the open board into the address without navigating, so a reload
 * or a shared link lands on it and nothing on the page remounts.
 */
export const rememberBoardInUrl = (key: string): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(BOARD_PARAM, key);
    window.history.replaceState(window.history.state, "", url);
  } catch {
    // Not in a browser, or history refused: the board is still open.
  }
};

/**
 * The board an address names, the page's own by default: what
 * rememberBoardInUrl writes there, the router's params do not follow. Null
 * for an address without one, or outside a browser.
 */
export const boardInUrl = (href?: string): string | null => {
  try {
    return new URL(href ?? window.location.href).searchParams.get(BOARD_PARAM);
  } catch {
    return null;
  }
};
